import { singleCount } from "../../../dashboard-framework/aggregateRows";
import { nextWeekdaysRange, nextWorkDaysRange } from "../../../dashboard-framework/datePresets";
import { formatDateRangePt } from "../../../dashboard-framework/describeWidgetLogic";
import { DrillDownQueryOptions, drillDownFromAggregate } from "../../../dashboard-framework/drilldown";
import { useAggregate } from "../../../dashboard-framework/useAggregate";
import { GaugeCard } from "../../../dashboard-framework/widgets/GaugeCard";
import { SESSIONS_DRILL_FIELDS, sessionRoute, sessionsDrillColumns } from "./drillColumns";
import { LEAD_TYPE_CHIPS_TEXT, LEAD_TYPE_CHIPS_WARNING, leadTypeChipsFromSessions, SESSION_LEAD_TYPE_TEXT, SessionLeadType } from "./leadClientTypes";
import { useSessionsByLeadType } from "./useSessionsByLeadType";
import {
    EVALUATIONS_HAPPENING_STATUSES,
    EVALUATIONS_NEXT_WORK_DAYS_GOAL,
    evaluationsCountQuery,
    REPORTS_NEXT_WORK_DAYS_GOAL,
    reportsCountQuery,
} from "./sharedMetrics";
import { FIRST_EEG_SERVICE, REPORTS_FIRST_SERVICE } from "../../../utils/constants";

// Upcoming-funnel gauges (booked qEEG evaluations + evaluation consultations), shown on digital-marketing
// and on reception-kpis. One single component because both dashboards must show EXACTLY the same number:
// sharing the query spec and the filter makes both pages land on the same React Query cache entry (both
// records are data_scope=user, hence the same "viewer" transport).
// The drill still obeys each dashboard's drill_roles — the DrillDownProvider belongs to the page.
// The NUMBER is always the same; what varies per dashboard is presentation: the child/adult breakdown is a
// management reading (it watches campaign segmentation) and stays OUT of the operational reception-kpis.

// Evaluations window: the next 6 work days including today. Saturday is a work day at the clinic, so
// from a Monday the window closes on the Saturday of the same week (only Sundays are skipped).
const WORK_DAY_COUNT = 6;
const WORK_DAYS_LABEL = `${WORK_DAY_COUNT} dias úteis`;

// Reports window: the next 5 weekdays including today — Saturday is left out by Jorge's decision (2026-10-20).
const REPORTS_WEEKDAY_COUNT = 5;
const REPORTS_WEEKDAYS_LABEL = `${REPORTS_WEEKDAY_COUNT} dias úteis`;
const SUNDAY_AND_SATURDAY_WEEKDAY_NUMBERS = [0, 6];

type EvaluationsDrillBase = Omit<DrillDownQueryOptions, "title" | "queryDescription">;

type UpcomingEvaluationsGaugesProps = {
    showClientTypeChips?: boolean;
    evaluationsDrillBase?: EvaluationsDrillBase;
};

export const UpcomingEvaluationsGauges = ({ showClientTypeChips = true, evaluationsDrillBase }: UpcomingEvaluationsGaugesProps) => {
    const now = new Date();
    const upcomingWindow = nextWorkDaysRange(now, WORK_DAY_COUNT);
    const upcomingWindowFilter = { start_date: { _gte: upcomingWindow.gte, _lt: upcomingWindow.lt } };
    const reportsWindow = nextWeekdaysRange(now, REPORTS_WEEKDAY_COUNT);

    const evaluationsFilter = { _and: [{ status: { _in: EVALUATIONS_HAPPENING_STATUSES } }, upcomingWindowFilter] };
    const reportsFilter = {
        _and: [
            { status: { _in: EVALUATIONS_HAPPENING_STATUSES } },
            { start_date: { _gte: reportsWindow.gte, _lt: reportsWindow.lt } },
            { "weekday(start_date)": { _nin: SUNDAY_AND_SATURDAY_WEEKDAY_NUMBERS } },
        ],
    };

    const evaluationsResult = useAggregate(evaluationsCountQuery, { filter: evaluationsFilter });
    const reportsResult = useAggregate(reportsCountQuery, { filter: reportsFilter });
    const evaluationsByTypeResult = useSessionsByLeadType(
        ["eval-happening-next-work-days", upcomingWindow.gte, upcomingWindow.lt],
        { _and: [{ service: { _eq: FIRST_EEG_SERVICE } }, evaluationsFilter] },
        showClientTypeChips
    );
    const reportsByTypeResult = useSessionsByLeadType(
        ["reports-next-weekdays", reportsWindow.gte, reportsWindow.lt],
        { _and: [{ service: { _eq: REPORTS_FIRST_SERVICE } }, reportsFilter] },
        showClientTypeChips
    );

    // Without chips the explanation must not mention them — it would describe something the card omits.
    const chipsText = showClientTypeChips ? ` ${LEAD_TYPE_CHIPS_TEXT} ${SESSION_LEAD_TYPE_TEXT}` : "";
    const chipsWarning = showClientTypeChips ? LEAD_TYPE_CHIPS_WARNING : undefined;
    const explanations = {
        evaluations: {
            queryDescription: `Conta as sessões de Avaliação qEEG - Primeira marcadas, confirmadas, em espera na clínica, em sessão ou realizadas nos próximos ${WORK_DAYS_LABEL} a contar de hoje (${formatDateRangePt(
                upcomingWindow
            )}).${chipsText}`,
            warningNote: chipsWarning,
        },
        reports: {
            queryDescription: `Conta as Consultas de Avaliação marcadas, confirmadas, em espera na clínica, em sessão ou realizadas nos próximos ${REPORTS_WEEKDAYS_LABEL} a contar de hoje, de segunda a sexta (${formatDateRangePt(
                reportsWindow
            )}; os sábados ficam de fora).${chipsText}`,
            warningNote: chipsWarning,
        },
    };

    const drillBase = { fieldsQuery: SESSIONS_DRILL_FIELDS, defaultSort: "start_date", columns: sessionsDrillColumns, rowRoute: sessionRoute };
    const evaluationsTitle = `Avaliações marcadas próx. ${WORK_DAYS_LABEL}`;
    const reportsTitle = `Consultas de avaliação próx. ${REPORTS_WEEKDAYS_LABEL}`;

    return (
        <>
            <GaugeCard
                title={evaluationsTitle}
                value={evaluationsResult.data ? singleCount(evaluationsResult.data) : undefined}
                goal={EVALUATIONS_NEXT_WORK_DAYS_GOAL}
                explanation={explanations.evaluations}
                isLoading={evaluationsResult.isLoading}
                isError={evaluationsResult.isError}
                drillDown={drillDownFromAggregate(evaluationsCountQuery, evaluationsFilter, {
                    title: evaluationsTitle,
                    queryDescription: explanations.evaluations.queryDescription,
                    ...(evaluationsDrillBase ?? drillBase),
                })}
                chips={evaluationsByTypeResult.data ? leadTypeChipsFromSessions((evaluationsByTypeResult.data.items || []) as Array<SessionLeadType>) : undefined}
            />
            <GaugeCard
                title={reportsTitle}
                value={reportsResult.data ? singleCount(reportsResult.data) : undefined}
                goal={REPORTS_NEXT_WORK_DAYS_GOAL}
                explanation={explanations.reports}
                isLoading={reportsResult.isLoading}
                isError={reportsResult.isError}
                drillDown={drillDownFromAggregate(reportsCountQuery, reportsFilter, {
                    title: reportsTitle,
                    queryDescription: explanations.reports.queryDescription,
                    ...drillBase,
                })}
                chips={reportsByTypeResult.data ? leadTypeChipsFromSessions((reportsByTypeResult.data.items || []) as Array<SessionLeadType>) : undefined}
            />
        </>
    );
};
